// src/lib/device.js
// Per-device identifier for HMRC fraud prevention headers.
//
// Gov-Client-Device-ID must be a genuinely unique identifier for the device the
// customer is using, generated once and kept for the life of that browser
// install. It must not be a constant, and it must not follow a guessable
// pattern — HMRC's validator rejects sequential or constructed-looking values.
//
// We generate a random v4 UUID with the browser's crypto API and persist it in
// localStorage, so each browser on each machine reports its own stable ID.

const STORAGE_KEY = 'quarterly_device_id'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function randomUUID() {
  // crypto.randomUUID is available in all browsers we support over HTTPS.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: build a v4 UUID from cryptographically secure random bytes.
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  b[6] = (b[6] & 0x0f) | 0x40   // version 4
  b[8] = (b[8] & 0x3f) | 0x80   // variant 10
  const hex = [...b].map(x => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`
}

export function getDeviceId() {
  try {
    const existing = localStorage.getItem(STORAGE_KEY)
    if (existing && UUID_RE.test(existing)) return existing
    const fresh = randomUUID()
    localStorage.setItem(STORAGE_KEY, fresh)
    return fresh
  } catch {
    // Private browsing or storage disabled: still send a valid, random ID for
    // this session rather than a constant.
    return randomUUID()
  }
}

// Screen and window dimensions, captured together so they describe the same
// moment on the same display.
export function getScreenHeaders() {
  return {
    screens:    `width=${window.screen.width}&height=${window.screen.height}` +
                `&scaling-factor=${window.devicePixelRatio || 1}` +
                `&colour-depth=${window.screen.colorDepth}`,
    windowSize: `width=${window.outerWidth}&height=${window.outerHeight}`,
  }
}

// HMRC fraud prevention requires the client's public (ephemeral) port, which
// browsers only expose through a WebRTC STUN candidate.
export function getClientPublicPort() {
  return new Promise(resolve => {
    let port = ''
    try {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] })
      pc.createDataChannel('')
      pc.createOffer().then(o => pc.setLocalDescription(o)).catch(() => {})
      pc.onicecandidate = e => {
        const m = e.candidate?.candidate?.match(/udp\s+\d+\s+[\d.]+\s+(\d+)/i)
        if (m) { port = m[1]; try { pc.close() } catch {} ; resolve(port) }
      }
      setTimeout(() => resolve(port), 3000)
    } catch { resolve('') }
  })
}
